import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { environment } from '../../environments/environment';
import { RuleCreateRequest } from '../configurator/configurator.models';
import { ConfiguratorApiService } from '../core/api/configurator-api.service';
import { I18nService } from '../core/i18n/i18n.service';
import { TranslatePipe } from '../core/i18n/translate.pipe';

@Component({
  selector: 'app-rule-admin-page',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './rule-admin-page.component.html',
  styleUrl: './shared-page.css'
})
export class RuleAdminPageComponent {
  private readonly api = inject(ConfiguratorApiService);
  private readonly i18n = inject(I18nService);

  productId = environment.defaultProductId;
  rulesetName = 'Standard-Kompatibilitaetsregeln';
  rulesetId = '';
  ruleName = 'Schiebedach erfordert Premium-Dach';
  ruleSeverity: 'ERROR' | 'WARN' = 'ERROR';
  ruleMessage = 'SUNROOF erfordert PREMIUM_ROOF';
  ruleLeftOptionCode = 'SUNROOF';
  ruleRightOptionCode = 'PREMIUM_ROOF';

  busy = false;
  error = '';
  info = '';

  createRuleSet(): void {
    if (!this.productId.trim()) {
      this.error = this.i18n.t('rule.error.productIdRequired');
      return;
    }
    this.error = '';
    this.info = '';
    this.busy = true;
    this.api.createRuleSet(this.productId.trim(), this.rulesetName.trim())
      .pipe(finalize(() => this.busy = false))
      .subscribe({
        next: (rulesetId) => {
          this.rulesetId = rulesetId;
          this.info = this.i18n.t('rule.info.rulesetCreated', { id: rulesetId });
        },
        error: (err) => this.error = this.extractError(err)
      });
  }

  addRule(): void {
    if (!this.rulesetId.trim()) {
      this.error = this.i18n.t('rule.error.rulesetIdRequired');
      return;
    }
    const payload: RuleCreateRequest = {
      name: this.ruleName.trim(),
      severity: this.ruleSeverity,
      expression: {
        op: 'IMPLIES',
        left: { op: 'SELECTED', optionCode: this.ruleLeftOptionCode.trim() },
        right: { op: 'SELECTED', optionCode: this.ruleRightOptionCode.trim() }
      },
      message: this.ruleMessage.trim(),
      dslVersion: '1.0'
    };

    this.error = '';
    this.info = '';
    this.busy = true;
    this.api.createRule(this.rulesetId.trim(), payload)
      .pipe(finalize(() => this.busy = false))
      .subscribe({
        next: (ruleId) => this.info = this.i18n.t('rule.info.ruleCreated', { id: ruleId }),
        error: (err) => this.error = this.extractError(err)
      });
  }

  publishRuleSet(): void {
    if (!this.rulesetId.trim()) {
      this.error = this.i18n.t('rule.error.rulesetIdRequired');
      return;
    }
    this.error = '';
    this.info = '';
    this.busy = true;
    this.api.publishRuleSet(this.rulesetId.trim())
      .pipe(finalize(() => this.busy = false))
      .subscribe({
        next: () => this.info = this.i18n.t('rule.info.rulesetPublished'),
        error: (err) => this.error = this.extractError(err)
      });
  }

  private extractError(err: unknown): string {
    if (typeof err === 'object' && err !== null && 'error' in err) {
      const payload = (err as { error?: { error?: string } }).error;
      if (payload?.error) {
        return payload.error;
      }
    }
    return this.i18n.t('common.requestFailedJwt');
  }
}
